'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { industriesByJobType, type Industry, allIndustries } from "@/doi-tac-module/lib/industry-data";
import { Briefcase, DollarSign, Dna, Search, SlidersHorizontal, Building, UserSearch, GraduationCap, Languages, User, Ruler, Weight, ImageIcon, Heart, MapPin } from "lucide-react";
import { cn } from '@/doi-tac-module/lib/utils';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { Badge } from '@/components/ui/badge';
import { japanJobTypes, visaDetailsByVisaType, dominantHands, educationLevels, japaneseLevels, visionRequirements, tattooRequirements } from '@/doi-tac-module/lib/visa-data';
import { type SearchFilters, getIndustryKeyByLaborType } from '@/doi-tac-module/lib/candidate-filter-utils';
import { FilterListWithSearch } from './FilterListWithSearch';
import { hobbiesList, allergiesList } from '@/doi-tac-module/lib/hobbies-allergies-data';
import { bangladeshDivisions } from '@/doi-tac-module/lib/location-data';

const DebouncedSalaryInput = ({
    value,
    isFrom,
    isUnder,
    minField,
    maxField,
    onFilterChange,
    disabled
}: {
    value: string | number,
    isFrom: boolean,
    isUnder: boolean,
    minField: keyof SearchFilters,
    maxField: keyof SearchFilters,
    onFilterChange: (newFilters: Partial<SearchFilters>) => void,
    disabled: boolean
}) => {
    const [localValue, setLocalValue] = useState(String(value || ''));

    useEffect(() => {
        setLocalValue(String(value || ''));
    }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const num = localValue === '' ? undefined : Number(localValue);
            if (num !== (value === '' ? undefined : Number(value))) {
                if (isFrom) onFilterChange({ [minField]: num, [maxField]: undefined });
                else if (isUnder) onFilterChange({ [minField]: undefined, [maxField]: num });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [localValue, onFilterChange, isFrom, isUnder, minField, maxField, value]);

    return (
        <div className="relative pt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <Input
                type="text"
                placeholder={(agencyTranslations.candidateSearch?.banner?.placeholders?.salaryUnder?.[useLanguage().language] || 'Under {val}').replace('{val}', '500')}
                className="pl-7 h-9 text-xs bg-white"
                disabled={disabled}
                value={localValue}
                onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setLocalValue(val);
                }}
            />
        </div>
    );
};

const DebouncedInput = ({
    value,
    onValueChange,
    placeholder,
    className,
    icon: Icon
}: {
    value: string,
    onValueChange: (val: string) => void,
    placeholder: string,
    className?: string,
    icon?: any
}) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localValue !== value) {
                onValueChange(localValue);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [localValue, onValueChange, value]);

    return (
        <div className="relative">
            {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />}
            <Input
                placeholder={placeholder}
                className={cn(Icon && "pl-9", "h-10", className)}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
            />
        </div>
    );
};


interface FilterSidebarProps {
    filters: SearchFilters;
    appliedFilters: SearchFilters;
    onFilterChange: (newFilters: Partial<SearchFilters>) => void;
    onApply: () => void;
    onReset: () => void;
    resultCount: number;
    isCounting?: boolean;
    agencies?: any[];
    currentUser?: any;
}

const visionOptions = ['10/10', '9/10', '8/10', '7/10', '6/10', '5/10', 'Dưới 5/10', 'Cận', 'Viễn', 'Loạn'];
const tattooOptions = ['Không có', 'Nhỏ, kín', 'To, lộ'];
const habitOptions = ['Có', 'Không', 'Thỉnh thoảng'];

const createSlug = (str: string) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "d")
        .replace(/\s+/g, '-')
        .replace(/[^\w\-.]+/g, '');
};

const handleSalaryInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof SearchFilters,
    limit: number | null,
    onFilterChange: FilterSidebarProps['onFilterChange']
) => {
    const rawValue = e.target.value;
    let num = Number(rawValue.replace(/[^0-9]/g, ''));

    if (isNaN(num)) {
        onFilterChange({ [field]: '' });
        return;
    }

    if (limit && num > limit) {
        num = limit;
    }

    onFilterChange({ [field]: String(num) });
};

const getDisplayValue = (value?: string) => {
    if (!value) return '';
    const num = Number(String(value).replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('ja-JP');
};

const MonthlySalaryContent = React.memo(({ filters, onFilterChange }: Pick<FilterSidebarProps, 'filters' | 'onFilterChange'>) => {
    const { language } = useLanguage();
    const t = agencyTranslations.candidateSearch;
    const renderSalarySection = (
        title: string,
        minField: keyof SearchFilters,
        maxField: keyof SearchFilters
    ) => {
        const minVal = filters[minField] as number | undefined;
        const maxVal = filters[maxField] as number | undefined;
        const isAll = minVal === undefined && maxVal === undefined;
        const isFrom = minVal !== undefined;
        const isUnder = maxVal !== undefined;

        const currentVal = minVal ?? maxVal ?? '';

        return (
            <div className="space-y-3">
                <Label className="text-sm text-slate-500">{title}</Label>
                <div className="grid grid-cols-3 gap-y-3 gap-x-2 pt-1">
                    {[{ id: 'all', label: agencyTranslations.candidateSearch.filters.options.salaryRange.all[useLanguage().language] },
                    { id: 'from', label: agencyTranslations.candidateSearch.filters.options.salaryRange.from[useLanguage().language] },
                    { id: 'under', label: agencyTranslations.candidateSearch.filters.options.salaryRange.under[useLanguage().language] }].map((mode) => {
                        const isActive =
                            mode.id === 'all' ? isAll :
                                mode.id === 'from' ? isFrom :
                                    mode.id === 'under' ? isUnder : false;

                        return (
                            <div
                                key={`salary-mode-${mode.id}`}
                                className="flex items-center gap-2 cursor-pointer group"
                                onClick={() => {
                                    const val = typeof currentVal === 'number' ? currentVal : 500;
                                    if (mode.id === 'all') onFilterChange({ [minField]: undefined, [maxField]: undefined });
                                    else if (mode.id === 'from') onFilterChange({ [minField]: val, [maxField]: undefined });
                                    else if (mode.id === 'under') onFilterChange({ [minField]: undefined, [maxField]: val });
                                }}
                            >
                                <div className={cn(
                                    "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                                    isActive ? "border-primary bg-white" : "border-slate-300 group-hover:border-primary/50"
                                )}>
                                    {isActive && <div className="w-2.5 h-2.5 rounded-full bg-primary animate-in zoom-in-50 duration-200" />}
                                </div>
                                <span className={cn(
                                    "text-xs transition-colors whitespace-nowrap",
                                    isActive ? "text-slate-900 font-semibold" : "text-slate-500 group-hover:text-slate-700"
                                )}>
                                    {mode.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <DebouncedSalaryInput
                    value={currentVal}
                    isFrom={isFrom}
                    isUnder={isUnder}
                    minField={minField}
                    maxField={maxField}
                    onFilterChange={onFilterChange}
                    disabled={isAll}
                />
            </div>
        );
    };

    return (
        <div className="space-y-6 pb-2">
            {renderSalarySection(t.filters?.labels?.salaryCurrent?.[language] || 'Current Salary', 'currentSalaryMin', 'currentSalaryMax')}
            {renderSalarySection(t.filters?.labels?.salaryVn?.[language] || 'Salary VN', 'desiredSalaryVietnamMin', 'desiredSalaryVietnamMax')}
        </div>
    );
});
MonthlySalaryContent.displayName = 'MonthlySalaryContent';

/* Hierarchical Filter Group Component */
const HierarchicalFilter = ({
    id,
    label,
    isChecked,
    onToggleParent,
    children,
    selectedChildren,
    onToggleChild
}: {
    id: string;
    label: string;
    isChecked: boolean;
    onToggleParent: (checked: boolean) => void;
    children: string[];
    selectedChildren: string[];
    onToggleChild: (child: string, checked: boolean) => void;
}) => (
    <div className="space-y-2">
        <div className="flex items-center space-x-2">
            <Checkbox
                id={id}
                checked={isChecked}
                onCheckedChange={(checked) => onToggleParent(!!checked)}
            />
            <Label htmlFor={id} className="text-sm font-semibold cursor-pointer">{label}</Label>
        </div>
        {isChecked && (
            <div className="ml-6 space-y-2 pt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {children.map((child, cIdx) => (
                    <div key={`${id}-child-${cIdx}`} className="flex items-center space-x-2">
                        <Checkbox
                            id={`${id}-${child}`}
                            checked={selectedChildren.includes(child)}
                            onCheckedChange={(checked) => onToggleChild(child, !!checked)}
                        />
                        <Label htmlFor={`${id}-${child}`} className="text-sm font-normal cursor-pointer text-slate-600">{child}</Label>
                    </div>
                ))}
            </div>
        )}
    </div>
);

export const FilterSidebar = React.memo(({
    filters,
    appliedFilters,
    onFilterChange,
    onApply,
    onReset,
    resultCount,
    isCounting = false,
    agencies = [],
    currentUser
}: FilterSidebarProps) => {
    const { language } = useLanguage();
    const { isMasterAgent, user } = useAuth();

    // Only ADMIN can see the agency filter
    const canSeeAgencyFilter = currentUser?.role === 'ADMIN' ||
        (user as any)?.role === 'ADMIN';

    const t = agencyTranslations.candidateSearch;
    const [availableJobDetails, setAvailableJobDetails] = useState<string[]>([]);
    const [availableIndustries, setAvailableIndustries] = useState<Industry[]>(allIndustries);

    const allJobDetailsForExperience = useMemo(() => {
        return [...new Set(Object.values(industriesByJobType).flat().flatMap(ind => ind.keywords).filter(Boolean))];
    }, []);

    useEffect(() => {
        const parentVisaSlug = getIndustryKeyByLaborType(filters.visa) ||
            Object.keys(industriesByJobType).find(key =>
                (visaDetailsByVisaType[key as keyof typeof visaDetailsByVisaType] || []).some(detail => detail.slug === filters.visaDetail)
            );

        const industries = parentVisaSlug ? (industriesByJobType[parentVisaSlug as keyof typeof industriesByJobType] || allIndustries) : allIndustries;
        const uniqueIndustries = Array.from(new Map(industries.map(item => [item.name, item])).values());
        setAvailableIndustries(uniqueIndustries);

        if (filters.industry && filters.industry !== 'all') {
            const selectedIndustryData = allIndustries.find(ind => ind.name === filters.industry);
            setAvailableJobDetails(selectedIndustryData?.keywords || []);
        } else {
            const allJobDetails = uniqueIndustries.flatMap(ind => ind.keywords);
            setAvailableJobDetails([...new Set(allJobDetails)]);
        }
    }, [filters.visa, filters.visaDetail, filters.industry]);

    const activeFilters = { ...appliedFilters, ...filters };

    const experienceYears = [
        { name: t.filters?.options?.experience?.noRequirement?.[language] || 'No requirement', value: 'Không yêu cầu', slug: 'khong-yeu-cau' },
        { name: (t.filters?.options?.experience?.under?.[language] || 'Under') + ' 1 ' + (t.filters?.options?.experience?.years?.[language] || 'years'), value: 'Dưới 1 năm', slug: 'duoi-1-nam' },
        { name: '1-2 ' + (t.filters?.options?.experience?.years?.[language] || 'years'), value: '1-2 năm', slug: '1-2-nam' },
        { name: '2-3 ' + (t.filters?.options?.experience?.years?.[language] || 'years'), value: '2-3 năm', slug: '2-3-nam' },
        { name: (t.filters?.options?.experience?.from?.[language] || 'From') + ' 3 ' + (t.filters?.options?.experience?.years?.[language] || 'years'), value: 'Trên 3 năm', slug: 'tren-3-nam' },
    ];

    const availableExperienceJobDetails = useMemo(() => {
        if (filters.experienceIndustry && filters.experienceIndustry !== 'all') {
            const ind = allIndustries.find(i => i.name === filters.experienceIndustry);
            return ind?.keywords || [];
        }
        return allJobDetailsForExperience;
    }, [filters.experienceIndustry, allJobDetailsForExperience]);

    return (
        <Card className="relative flex-1 flex flex-col shadow-none border-none lg:border lg:shadow-sm bg-white">
            <CardHeader className="pb-3 px-4">
                <CardTitle className="text-xl flex items-center gap-2">
                    <SlidersHorizontal className="h-5 w-5 text-primary" />
                    {t.filters?.titles?.candidateFilters?.[language] || 'Filters'}
                </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow px-4">
                <Accordion type="multiple" defaultValue={['jobType', 'industry', 'salary', 'english', 'gender', 'experience', 'bodyStats', 'educationLanguage', 'personalInfo', 'physicalHealth', 'mediaPresence']} className="w-full">

                    {/* Keyword Search */}
                    <div className="mb-6 space-y-3">
                        <Label className="text-lg font-semibold flex items-center gap-2 text-slate-900">
                            <Search className="h-4 w-4 text-primary" />
                            {t.filters?.titles?.keywordSearch?.[language] || 'Search'}
                        </Label>
                        <DebouncedInput
                            value={filters.keyword || ''}
                            onValueChange={(val) => onFilterChange({ keyword: val })}
                            placeholder={t.filters?.labels?.keywordPlaceholder?.[language] || 'Keyword...'}
                        />
                    </div>

                    {/* Job Type */}
                    <AccordionItem value="jobType" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-accent" />{t.filters?.titles?.jobInfo?.[language] || 'Job Information'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-2">
                            {canSeeAgencyFilter && (
                                <div className="space-y-2">
                                    <Label className="text-sm text-slate-500">{t.filters?.labels?.agency?.[language] || 'Agency'}</Label>
                                    <Select
                                        value={filters.bangladeshAgency || 'all'}
                                        onValueChange={(v) => onFilterChange({ bangladeshAgency: v === 'all' ? '' : v })}
                                    >
                                        <SelectTrigger className={cn("h-9", filters.bangladeshAgency && 'text-primary border-primary/30')}>
                                            <SelectValue placeholder={t.filters?.labels?.allAgencies?.[language] || 'All Agencies'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t.filters?.labels?.allAgencies?.[language] || 'All Agencies'}</SelectItem>
                                            {agencies.map((agency, aIdx) => (
                                                <SelectItem key={`agency-${agency.uid || aIdx}-${aIdx}`} value={agency.uid}>
                                                    {agency.companyName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.laborType?.[language] || 'Visa'}</Label>
                                <Select
                                    value={filters.visa || 'all'}
                                    onValueChange={(v) => {
                                        const newVisa = v === 'all' ? '' : v;
                                        const payload: Partial<SearchFilters> = { visa: newVisa, visaDetail: '' };
                                        if (filters.industry && filters.industry !== 'all') {
                                            const visaKey = getIndustryKeyByLaborType(newVisa);
                                            const newIndustries = visaKey ? (industriesByJobType[visaKey] || allIndustries) : allIndustries;
                                            const isStillValid = newIndustries.some(ind => ind.name === filters.industry);
                                            if (!isStillValid) {
                                                payload.industry = '';
                                                payload.jobDetail = '';
                                            }
                                        }
                                        onFilterChange(payload);
                                    }}
                                >
                                    <SelectTrigger className={cn("h-9", filters.visa && filters.visa !== 'all' && 'text-primary border-primary/30')}>
                                        <SelectValue placeholder={t.filters?.labels?.laborType?.[language] || 'Visa'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'All'}</SelectItem>
                                        <SelectItem value="Lao động phổ thông">{agencyTranslations.candidateSearch?.banner?.laborTypes?.unskilled?.[language] || 'Unskilled'}</SelectItem>
                                        <SelectItem value="Lao động kỹ thuật">{agencyTranslations.candidateSearch?.banner?.laborTypes?.skilled?.[language] || 'Skilled'}</SelectItem>
                                        <SelectItem value="Kỹ sư, chuyên gia">{agencyTranslations.candidateSearch?.banner?.laborTypes?.engineer?.[language] || 'Engineer'}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Industry */}
                    <AccordionItem value="industry" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Building className="h-4 w-4 text-accent-orange" />{t.filters?.titles?.industrySkills?.[language] || 'Industry'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-2">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.industry?.[language] || 'industry'}</Label>
                                <Select value={filters.industry || 'all'} onValueChange={(value) => onFilterChange({ industry: value === 'all' ? '' : value, jobDetail: '' })}>
                                    <SelectTrigger className={cn("h-9", filters.industry && filters.industry !== 'all' && 'text-primary border-primary/30')}>
                                        <SelectValue placeholder={t.filters?.labels?.allIndustries?.[language] || 'All Industries'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        <SelectItem value="all">{t.filters?.labels?.allIndustries?.[language] || 'allIndustries'}</SelectItem>
                                        {availableIndustries.map((ind, iIdx) => (
                                            <SelectItem key={`industry-${ind.slug || iIdx}-${iIdx}`} value={ind.name}>
                                                {agencyTranslations.candidateSearch?.industryNames?.[ind.name]?.[language] || ind.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.jobDetail?.[language] || 'Detailed Industry'}</Label>
                                <Select value={filters.jobDetail || 'all-details'} onValueChange={(value) => onFilterChange({ jobDetail: value === 'all-details' ? '' : value })} disabled={!filters.industry || filters.industry === 'all'}>
                                    <SelectTrigger className={cn("h-9", filters.jobDetail && 'text-primary border-primary/30')}>
                                        <SelectValue placeholder={t.filters?.labels?.allJobDetails?.[language] || 'All Details'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        <SelectItem value="all-details">{t.filters?.labels?.allJobDetails?.[language] || 'All Details'}</SelectItem>
                                        {availableJobDetails.map((detail, dIdx) => (
                                            <SelectItem key={`job-detail-${detail}-${dIdx}`} value={detail}>
                                                {t.industryDetailNames?.[detail]?.[language] || detail}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Salary */}
                    <AccordionItem value="salary" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-accent-green" />{t.filters?.titles?.salary?.[language] || 'Salary'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">
                            <MonthlySalaryContent filters={filters} onFilterChange={onFilterChange} />
                        </AccordionContent>
                    </AccordionItem>

                    {/* Gender */}
                    <AccordionItem value="gender" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><User className="h-4 w-4 text-primary" />{t.filters?.titles?.gender?.[language] || 'Gender'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">
                            <Select value={filters.gender || 'all'} onValueChange={(v) => onFilterChange({ gender: v === 'all' ? '' : v as any })}>
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'All'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'All'}</SelectItem>
                                    <SelectItem value="nam">{t.filters?.options?.gender?.male?.[language] || 'Male'}</SelectItem>
                                    <SelectItem value="nu">{t.filters?.options?.gender?.female?.[language] || 'Female'}</SelectItem>
                                </SelectContent>
                            </Select>
                        </AccordionContent>
                    </AccordionItem>

                    {/* English */}
                    <AccordionItem value="english" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Languages className="h-4 w-4 text-accent" />{t.filters?.titles?.english?.[language] || 'English'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.englishCert?.[language] || 'Certificate'}</Label>
                                <Select value={filters.englishScore || 'all'} onValueChange={(v) => onFilterChange({ englishScore: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        <SelectItem value="none">{t.filters?.options?.languageLevels?.none?.[language] || 'none'}</SelectItem>
                                        <SelectItem value="TOEIC 400+">TOEIC 400+</SelectItem>
                                        <SelectItem value="TOEIC 500+">TOEIC 500+</SelectItem>
                                        <SelectItem value="TOEIC 600+">TOEIC 600+</SelectItem>
                                        <SelectItem value="TOEIC 700+">TOEIC 700+</SelectItem>
                                        <SelectItem value="TOEIC 800+">TOEIC 800+</SelectItem>
                                        <SelectItem value="TOEIC 900+">TOEIC 900+</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.englishComm?.[language] || 'Communication'}</Label>
                                <Select value={filters.englishLevel || 'all'} onValueChange={(v) => onFilterChange({ englishLevel: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        <SelectItem value="none">{t.filters?.options?.languageLevels?.none?.[language] || 'none'}</SelectItem>
                                        <SelectItem value="Cơ bản">{t.filters?.options?.languageLevels?.basic?.[language] || 'basic'}</SelectItem>
                                        <SelectItem value="Trung bình">{t.filters?.options?.languageLevels?.intermediate?.[language] || 'intermediate'}</SelectItem>
                                        <SelectItem value="Tốt">{t.filters?.options?.languageLevels?.good?.[language] || 'good'}</SelectItem>
                                        <SelectItem value="Bản ngữ">{t.filters?.options?.languageLevels?.native?.[language] || 'native'}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Vietnamese */}
                    <AccordionItem value="vietnamese" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Languages className="h-4 w-4 text-accent" />{t.filters?.titles?.vietnamese?.[language] || 'Vietnamese'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.vnCert?.[language] || 'Certificate'}</Label>
                                <Select value={filters.vietnameseCertificate || 'all'} onValueChange={(v) => onFilterChange({ vietnameseCertificate: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        <SelectItem value="none">{t.filters?.options?.languageLevels?.none?.[language] || 'none'}</SelectItem>
                                        <SelectItem value="A1">A1</SelectItem>
                                        <SelectItem value="A2">A2</SelectItem>
                                        <SelectItem value="B1">B1</SelectItem>
                                        <SelectItem value="B2">B2</SelectItem>
                                        <SelectItem value="C1">C1</SelectItem>
                                        <SelectItem value="C2">C2</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.vnComm?.[language] || 'Communication'}</Label>
                                <Select value={filters.vietnameseCommunication || 'all'} onValueChange={(v) => onFilterChange({ vietnameseCommunication: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        <SelectItem value="none">{t.filters?.options?.languageLevels?.none?.[language] || 'none'}</SelectItem>
                                        <SelectItem value="Cơ bản">{t.filters?.options?.languageLevels?.basic?.[language] || 'basic'}</SelectItem>
                                        <SelectItem value="Trung bình">{t.filters?.options?.languageLevels?.intermediate?.[language] || 'intermediate'}</SelectItem>
                                        <SelectItem value="Tốt">{t.filters?.options?.languageLevels?.good?.[language] || 'good'}</SelectItem>
                                        <SelectItem value="Bản ngữ">{t.filters?.options?.languageLevels?.native?.[language] || 'native'}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Experience */}
                    <AccordionItem value="experience" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-accent-orange" />{t.filters?.titles?.experience?.[language] || 'Experience'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-2">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.industry?.[language] || 'industry'}</Label>
                                <Select
                                    value={filters.experienceIndustry || 'all'}
                                    onValueChange={(value) => onFilterChange({ experienceIndustry: value === 'all' ? '' : value, experienceRequirement: '' })}
                                >
                                    <SelectTrigger className={cn("h-9", filters.experienceIndustry && 'text-primary border-primary/30')}>
                                        <SelectValue placeholder={t.filters?.labels?.allIndustries?.[language] || 'All Industries'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        <SelectItem value="all">{t.filters?.labels?.allIndustries?.[language] || 'allIndustries'}</SelectItem>
                                        {(availableIndustries || []).map((ind, iIdx) => (
                                            <SelectItem key={`exp-industry-${ind?.slug || iIdx}-${iIdx}`} value={ind?.name}>
                                                {agencyTranslations.candidateSearch?.industryNames?.[ind.name]?.[language] || ind.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.expDetail?.[language] || 'Detailed Exp'}</Label>
                                <Select value={filters.experienceRequirement || 'all'} onValueChange={(value) => onFilterChange({ experienceRequirement: value === 'all' ? '' : value })}>
                                    <SelectTrigger className={cn("h-9", filters.experienceRequirement && 'text-primary border-primary/30')}>
                                        <SelectValue placeholder={t.filters?.labels?.allJobDetails?.[language] || 'All Details'} />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                        <SelectItem value="all">{t.filters?.labels?.allJobDetails?.[language] || 'All Details'}</SelectItem>
                                        {(availableExperienceJobDetails || []).map((jobDetail, jIdx) => (
                                            <SelectItem key={`exp-detail-${jobDetail}-${jIdx}`} value={jobDetail}>
                                                {t.industryDetailNames?.[jobDetail]?.[language] || jobDetail}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500 font-semibold">{t.filters?.labels?.expYears?.[language] || 'Years of Exp'}</Label>
                                <Slider
                                    min={0}
                                    max={20}
                                    step={1}
                                    value={filters.yearsOfExperience || [0, 20]}
                                    onValueChange={(val) => onFilterChange({ yearsOfExperience: val as [number, number] })}
                                    className="py-1"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{(filters.yearsOfExperience || [0, 20])[0]} {t.filters?.options?.experience?.years?.[language] || 'years'}</span>
                                    <span>{(filters.yearsOfExperience || [0, 20])[1]} {t.filters?.options?.experience?.years?.[language] || 'years'}</span>
                                </div>
                            </div>
                            <div className="space-y-2 pt-2 border-t border-slate-50">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.workedAbroad?.[language] || 'Worked Abroad'}</Label>
                                <Select value={filters.hasWorkedAbroad || 'all'} onValueChange={(v) => onFilterChange({ hasWorkedAbroad: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'All'}</SelectItem>
                                        <SelectItem value="Rồi">{t.filters?.options?.experience?.worked?.[language] || 'Worked'}</SelectItem>
                                        <SelectItem value="Chưa">{t.filters?.options?.experience?.notWorked?.[language] || 'Not Worked'}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {filters.hasWorkedAbroad === 'Rồi' && (
                                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                                        <Label className="text-[10px] font-bold text-slate-400 uppercase">{t.filters?.labels?.workedCountry?.[language] || 'Countries'}</Label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {['Nhật Bản', 'Hàn Quốc', 'Đài Loan', 'Singapore', 'Malaysia', 'Trung Đông'].map(country => (
                                                <div key={`country-filter-${country}`} className="flex items-center gap-2">
                                                    <Checkbox
                                                        id={`country-${country}`}
                                                        checked={filters.workedCountries?.includes(country)}
                                                        onCheckedChange={(checked) => {
                                                            const currentSelection = new Set(filters.workedCountries || []);
                                                            if (checked) currentSelection.add(country);
                                                            else currentSelection.delete(country);
                                                            onFilterChange({ workedCountries: Array.from(currentSelection) });
                                                        }}
                                                    />
                                                    <Label htmlFor={`country-${country}`} className="text-[10px] cursor-pointer">
                                                        {t.filters.options.workedCountries[country as keyof typeof t.filters.options.workedCountries]?.[language] || country}
                                                    </Label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Body Stats */}
                    <AccordionItem value="bodyStats" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Ruler className="h-4 w-4 text-accent-green" />{t.filters?.titles?.bodyStats?.[language] || 'Body Stats'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-5">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500 font-semibold">{t.filters?.labels?.age?.[language] || 'Age'}</Label>
                                <Slider
                                    min={18}
                                    max={60}
                                    step={1}
                                    value={filters.age || [18, 45]}
                                    onValueChange={(val) => onFilterChange({ age: val as [number, number] })}
                                    className="py-1"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{filters.age ? filters.age[0] : 18} {(t.filters?.labels?.age?.[language] || 'Age').toLowerCase()}</span>
                                    <span>{filters.age ? filters.age[1] : 45} {(t.filters?.labels?.age?.[language] || 'Age').toLowerCase()}</span>
                                </div>
                            </div>
                            <div className="space-y-2 pt-1">
                                <Label className="text-sm text-slate-500 font-semibold">{t.filters?.labels?.height?.[language] || 'Height'}</Label>
                                <Slider
                                    min={140}
                                    max={220}
                                    step={1}
                                    value={filters.height || [140, 200]}
                                    onValueChange={(val) => onFilterChange({ height: val as [number, number] })}
                                    className="py-1"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{filters.height ? filters.height[0] : 140} cm</span>
                                    <span>{filters.height ? filters.height[1] : 200} cm</span>
                                </div>
                            </div>
                            <div className="space-y-2 pt-1">
                                <Label className="text-sm text-slate-500 font-semibold">{t.filters?.labels?.weight?.[language] || 'Weight'}</Label>
                                <Slider
                                    min={35}
                                    max={150}
                                    step={1}
                                    value={filters.weight || [35, 100]}
                                    onValueChange={(val) => onFilterChange({ weight: val as [number, number] })}
                                    className="py-1"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{filters.weight ? filters.weight[0] : 35} kg</span>
                                    <span>{filters.weight ? filters.weight[1] : 100} kg</span>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Education & Language */}
                    <AccordionItem value="educationLanguage" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary" />{t.filters?.titles?.education?.[language] || 'Education'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.education?.[language] || 'Education'}</Label>
                                <Select value={filters.educationRequirement || 'all'} onValueChange={(value) => onFilterChange({ educationRequirement: value === 'all' ? '' : value })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.education?.[language] || 'Education'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        {educationLevels.map(edu => <SelectItem key={edu.slug} value={edu.name}>{edu.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Personal Info */}
                    <AccordionItem value="personalInfo" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><UserSearch className="h-4 w-4 text-accent" />{t.filters?.titles?.familyStatus?.[language] || 'Family Status'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="text-sm text-slate-500">{t.filters?.labels?.marital?.[language] || 'Marital Status'}</Label>
                                    <Select value={filters.maritalStatus || 'all'} onValueChange={(v) => onFilterChange({ maritalStatus: v === 'all' ? '' : v })}>
                                        <SelectTrigger className="h-9">
                                            <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'All'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'All'}</SelectItem>
                                            <SelectItem value="Single">{t.filters?.options?.marital?.single?.[language] || 'Single'}</SelectItem>
                                            <SelectItem value="Married">{t.filters?.options?.marital?.married?.[language] || 'Married'}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    {/* Physical Health (Vision / Tattoo / Habits) */}
                    <AccordionItem value="physicalHealth" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><Dna className="h-4 w-4 text-accent-orange" />{t.filters?.titles?.health?.[language] || 'Health'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            {/* Vision */}
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.vision?.[language] || 'Vision'}</Label>
                                <Select value={filters.vision || 'all'} onValueChange={(v) => onFilterChange({ vision: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        {visionOptions.map((opt, oIdx) => {
                                            let label = opt;
                                            if (opt === 'Dưới 5/10') label = t.filters?.options?.vision?.under5?.[language] || opt;
                                            else if (opt === 'Cận') label = t.filters?.options?.vision?.nearsighted?.[language] || opt;
                                            else if (opt === 'Viễn') label = t.filters?.options?.vision?.farsighted?.[language] || opt;
                                            else if (opt === 'Loạn') label = t.filters?.options?.vision?.astigmatism?.[language] || opt;
                                            return <SelectItem key={`vision-${opt}-${oIdx}`} value={opt}>{label}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Tattoo */}
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.tattoo?.[language] || 'Tattoo'}</Label>
                                <Select value={filters.tattoo || 'all'} onValueChange={(v) => onFilterChange({ tattoo: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        {tattooOptions.map((opt, oIdx) => {
                                            let label = opt;
                                            if (opt === 'Không có') label = t.filters?.options?.tattoo?.none?.[language] || opt;
                                            else if (opt === 'Nhỏ, kín') label = t.filters?.options?.tattoo?.small?.[language] || opt;
                                            else if (opt === 'To, lộ') label = t.filters?.options?.tattoo?.large?.[language] || opt;
                                            return <SelectItem key={`tattoo-${opt}-${oIdx}`} value={opt}>{label}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Smoking */}
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.smoking?.[language] || 'Smoking'}</Label>
                                <Select value={filters.smokingStatus || 'all'} onValueChange={(v) => onFilterChange({ smokingStatus: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        {(habitOptions || []).map(opt => {
                                            let label = opt;
                                            if (opt === 'Có') label = t.filters?.options?.smoking?.yes?.[language] || 'yes';
                                            else if (opt === 'Không') label = t.filters?.options?.smoking?.no?.[language] || 'no';
                                            else if (opt === 'Thỉnh thoảng') label = t.filters?.options?.smoking?.sometimes?.[language] || 'sometimes';
                                            return <SelectItem key={opt} value={opt}>{label}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Drinking */}
                            <div className="space-y-2">
                                <Label className="text-sm text-slate-500">{t.filters?.labels?.drinking?.[language] || 'Drinking'}</Label>
                                <Select value={filters.drinkingStatus || 'all'} onValueChange={(v) => onFilterChange({ drinkingStatus: v === 'all' ? '' : v })}>
                                    <SelectTrigger className="h-9">
                                        <SelectValue placeholder={t.filters?.labels?.all?.[language] || 'all'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t.filters?.labels?.all?.[language] || 'all'}</SelectItem>
                                        {(habitOptions || []).map(opt => {
                                            let label = opt;
                                            if (opt === 'Có') label = t.filters?.options?.smoking?.yes?.[language] || 'yes';
                                            else if (opt === 'Không') label = t.filters?.options?.smoking?.no?.[language] || 'no';
                                            else if (opt === 'Thỉnh thoảng') label = t.filters?.options?.smoking?.sometimes?.[language] || 'sometimes';
                                            return <SelectItem key={opt} value={opt}>{label}</SelectItem>;
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="border-t border-slate-100 my-4" />

                            {/* Hobbies */}
                            <FilterListWithSearch
                                title={t.filters?.labels?.hobbies?.[language] || 'hobbies'}
                                items={hobbiesList}
                                selectedItems={filters.hobbies || []}
                                onItemsChange={(selected) => onFilterChange({ hobbies: selected })}
                                placeholder={t.filters?.labels?.searchHobbies?.[language] || 'Search hobbies...'}
                            />

                            {/* Allergies */}
                            <FilterListWithSearch
                                title={t.filters?.labels?.allergies?.[language] || 'Allergies'}
                                items={allergiesList}
                                selectedItems={filters.allergies || []}
                                onItemsChange={(selected) => onFilterChange({ allergies: selected })}
                                placeholder={t.filters?.labels?.searchAllergies?.[language] || 'Search allergies...'}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    {/* Location (Địa điểm) */}
                    <AccordionItem value="location" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent-green" />{t.filters?.labels?.location?.[language] || 'Location'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">
                            <FilterListWithSearch
                                title={t.filters?.labels?.city?.[language] || 'City'}
                                items={bangladeshDivisions.map(d => d.name)}
                                selectedItems={filters.location || []}
                                onItemsChange={(selected) => onFilterChange({ location: selected })}
                                placeholder={t.filters?.labels?.searchLocation?.[language] || 'Search location...'}
                            />
                        </AccordionContent>
                    </AccordionItem>

                    {/* Media Presence (Hierarchical) */}
                    <AccordionItem value="mediaPresence" className="border-slate-100">
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline py-3">
                            <span className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-primary" />{t.filters?.labels?.mediaPresence?.[language] || 'Media Presence'}</span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2 space-y-4">
                            <HierarchicalFilter
                                id="filter-video"
                                label={t.filters?.labels?.video?.[language] || 'Video'}
                                isChecked={filters.video === 'true'}
                                onToggleParent={(checked) => {
                                    onFilterChange({
                                        video: checked ? 'true' : '',
                                        videos: checked ? ['Video giới thiệu bản thân', 'Video tay nghề'] : []
                                    });
                                }}
                                children={[t.filters?.labels?.selfIntroVideo?.[language] || 'selfIntroVideo', t.filters?.labels?.skillVideo?.[language] || 'skillVideo']}
                                selectedChildren={(filters.videos || []).map(v =>
                                    v === 'Video giới thiệu bản thân' ? t.filters?.labels?.selfIntroVideo?.[language] || 'selfIntroVideo' :
                                        v === 'Video tay nghề' ? t.filters?.labels?.skillVideo?.[language] || 'skillVideo' : v
                                )}
                                onToggleChild={(child, checked) => {
                                    // Map back to original values for logic
                                    let originalChild = child;
                                    if (child === t.filters?.labels?.selfIntroVideo?.[language] || 'selfIntroVideo') originalChild = 'Video giới thiệu bản thân';
                                    else if (child === t.filters?.labels?.skillVideo?.[language] || 'skillVideo') originalChild = 'Video tay nghề';

                                    const set = new Set(filters.videos || []);
                                    if (checked) set.add(originalChild);
                                    else set.delete(originalChild);
                                    onFilterChange({ videos: Array.from(set) });
                                }}
                            />

                            <HierarchicalFilter
                                id="filter-photo"
                                label={t.filters?.labels?.photo?.[language] || 'photo'}
                                isChecked={filters.hasPhoto === 'true'}
                                onToggleParent={(checked) => {
                                    onFilterChange({
                                        hasPhoto: checked ? 'true' : '',
                                        photos: checked ? ['Ảnh chân dung', 'Ảnh toàn thân'] : []
                                    });
                                }}
                                children={[t.filters?.labels?.portraitPhoto?.[language] || 'portraitPhoto', t.filters?.labels?.fullPhoto?.[language] || 'fullPhoto']}
                                selectedChildren={(filters.photos || []).map(p =>
                                    p === 'Ảnh chân dung' ? t.filters?.labels?.portraitPhoto?.[language] || 'portraitPhoto' :
                                        p === 'Ảnh toàn thân' ? t.filters?.labels?.fullPhoto?.[language] || 'fullPhoto' : p
                                )}
                                onToggleChild={(child, checked) => {
                                    let originalChild = child;
                                    if (child === t.filters?.labels?.portraitPhoto?.[language] || 'portraitPhoto') originalChild = 'Ảnh chân dung';
                                    else if (child === t.filters?.labels?.fullPhoto?.[language] || 'fullPhoto') originalChild = 'Ảnh toàn thân';

                                    const set = new Set(filters.photos || []);
                                    if (checked) set.add(originalChild);
                                    else set.delete(originalChild);
                                    onFilterChange({ photos: Array.from(set) });
                                }}
                            />

                            <HierarchicalFilter
                                id="filter-docs"
                                label={t.filters?.labels?.docs?.[language] || 'docs'}
                                isChecked={filters.docs === 'true'}
                                onToggleParent={(checked) => {
                                    onFilterChange({
                                        docs: checked ? 'true' : '',
                                        docsSub: checked ? ['Hộ chiếu', 'Thẻ ID quốc gia', 'Lý lịch tư pháp', 'Giấy khám sức khoẻ', 'Bằng cấp'] : []
                                    });
                                }}
                                children={[
                                    t.filters?.labels?.passport?.[language] || 'passport',
                                    t.filters?.labels?.idCard?.[language] || 'idCard',
                                    t.filters?.labels?.pcc?.[language] || 'pcc',
                                    t.filters?.labels?.medical?.[language] || 'medical',
                                    t.filters?.labels?.degree?.[language] || 'degree'
                                ]}
                                selectedChildren={(filters.docsSub || []).map(d => {
                                    if (d === 'Hộ chiếu') return t.filters?.labels?.passport?.[language] || 'passport';
                                    if (d === 'Thẻ ID quốc gia') return t.filters?.labels?.idCard?.[language] || 'idCard';
                                    if (d === 'Lý lịch tư pháp') return t.filters?.labels?.pcc?.[language] || 'pcc';
                                    if (d === 'Giấy khám sức khoẻ') return t.filters?.labels?.medical?.[language] || 'medical';
                                    if (d === 'Bằng cấp') return t.filters?.labels?.degree?.[language] || 'degree';
                                    return d;
                                })}
                                onToggleChild={(child, checked) => {
                                    let originalChild = child;
                                    if (child === t.filters?.labels?.passport?.[language] || 'passport') originalChild = 'Hộ chiếu';
                                    else if (child === t.filters?.labels?.idCard?.[language] || 'idCard') originalChild = 'Thẻ ID quốc gia';
                                    else if (child === t.filters?.labels?.pcc?.[language] || 'pcc') originalChild = 'Lý lịch tư pháp';
                                    else if (child === t.filters?.labels?.medical?.[language] || 'medical') originalChild = 'Giấy khám sức khoẻ';
                                    else if (child === t.filters?.labels?.degree?.[language] || 'degree') originalChild = 'Bằng cấp';

                                    const set = new Set(filters.docsSub || []);
                                    if (checked) set.add(originalChild);
                                    else set.delete(originalChild);
                                    onFilterChange({ docsSub: Array.from(set) });
                                }}
                            />
                        </AccordionContent>
                    </AccordionItem>

                </Accordion>
            </CardContent>

            {/* Fixed Footer Buttons */}
            <div className="p-4 sticky bottom-0 bg-white/80 backdrop-blur-md border-t mt-auto z-30">
                <div className="grid grid-cols-3 gap-3 w-full">
                    <Button variant="outline" onClick={onReset} className="col-span-1 border-slate-100 text-slate-500 bg-white hover:bg-slate-50 hover:text-slate-600 transition-colors rounded-xl h-11">{t.filters?.buttons?.resetShort?.[language] || 'Xóa'}</Button>
                    <Button className="w-full bg-primary text-white col-span-2 font-medium rounded-xl h-11 shadow-sm hover:shadow-md hover:bg-primary/95 transition-all flex items-center justify-between px-4" onClick={onApply} disabled={isCounting}>
                        <span className="flex-1 text-center ml-4">{t.filters?.buttons?.apply?.[language] || 'Áp dụng'}</span>
                        <Badge variant="secondary" className="rounded-full px-2.5 py-1 bg-slate-100/90 text-slate-800 border-none font-bold text-sm min-w-[28px] flex justify-center">
                            {resultCount.toLocaleString('en-US')}
                        </Badge>
                    </Button>
                </div>
            </div>
        </Card>
    );
});
